/**
 * Tizen Web Device API implementation of PlatformApi.
 *
 * Used when the player is running inside the installed shell .wgt on
 * Samsung SSSP. Wraps the callback-based tizen.* APIs in promises and
 * presents the same surface as web.ts.
 *
 * Filesystem layout on device (paths visible via sdb shell):
 *   /opt/usr/home/owner/share/.../wgt-private/
 *     bundles/                 — cached player bundles
 *       player-X.Y.Z.js
 *       player-X.Y.Z.meta.json
 *     media/                   — cached media files
 *       <sha256>.mp4
 *       <sha256>.meta.json
 *
 * We use `wgt-private` virtual root so files survive across runs but
 * are cleaned on uninstall.
 */

import type {
  AlarmHandle,
  NetworkInfo,
  NetworkType,
  PlatformApi,
  PlatformAlarm,
  PlatformApplication,
  PlatformNetwork,
  PlatformPower,
  PlatformStorage,
  PlatformSystemInfo,
  PowerLock,
  StorageEntry,
  SystemInfo,
} from './types';

// Storage root resolved on demand inside ensureStorageRoot() — kept
// implicit because every platform op goes through that helper.
const META_SUFFIX = '.meta.json';

// ── Filesystem helpers (promise wrappers) ───────────────────────────

function resolvePath(path: string, mode: 'r' | 'rw' = 'rw'): Promise<TizenFile> {
  return new Promise((resolve, reject) => {
    tizen!.filesystem.resolve(
      path,
      resolve,
      (err) => reject(new Error(`resolve(${path}): ${err.name}: ${err.message}`)),
      mode,
    );
  });
}

async function ensureDir(parentPath: string, name: string): Promise<TizenFile> {
  const parent = await resolvePath(parentPath);
  try {
    return parent.resolve(name);
  } catch {
    return parent.createDirectory(name);
  }
}

async function ensureStorageRoot(): Promise<TizenFile> {
  // wgt-private always exists; just ensure our subdir.
  return ensureDir('wgt-private', 'storage');
}

function safeFilename(key: string): string {
  // Tizen filesystem doesn't accept some chars in filenames; URL-encode.
  return encodeURIComponent(key);
}

function blobToBytes(blob: Blob): Promise<Uint8Array> {
  return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
}

function writeBytesToFile(file: TizenFile, bytes: Uint8Array): Promise<void> {
  return new Promise((resolve, reject) => {
    file.openStream(
      'w',
      (stream) => {
        try {
          stream.writeBytes(bytes);
          stream.close();
          resolve();
        } catch (err) {
          reject(err);
        }
      },
      (err) => reject(new Error(`openStream w: ${err.name}: ${err.message}`)),
    );
  });
}

function readBytesFromFile(file: TizenFile): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    file.openStream(
      'r',
      (stream) => {
        try {
          const bytes = stream.readBytes(file.fileSize);
          stream.close();
          resolve(new Uint8Array(bytes));
        } catch (err) {
          reject(err);
        }
      },
      (err) => reject(new Error(`openStream r: ${err.name}: ${err.message}`)),
    );
  });
}

function readTextFromFile(file: TizenFile): Promise<string> {
  return new Promise((resolve, reject) => {
    file.readAsText(
      resolve,
      (err) => reject(new Error(`readAsText: ${err.name}: ${err.message}`)),
      'UTF-8',
    );
  });
}

function listChildren(dir: TizenFile): Promise<TizenFile[]> {
  return new Promise((resolve, reject) => {
    dir.listFiles(resolve, (err) =>
      reject(new Error(`listFiles: ${err.name}: ${err.message}`)),
    );
  });
}

// ── Storage ─────────────────────────────────────────────────────────

interface PersistedMeta {
  key: string;
  size: number;
  contentType: string | null;
  storedAt: number;
  filename: string;
}

class TizenStorage implements PlatformStorage {
  readonly persistent = true;
  private rootDir: TizenFile | null = null;
  private initPromise: Promise<void> | null = null;

  init(): Promise<void> {
    if (!this.initPromise) this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    this.rootDir = await ensureStorageRoot();
  }

  private root(): TizenFile {
    if (!this.rootDir) {
      throw new Error('TizenStorage.init() must complete before any I/O');
    }
    return this.rootDir;
  }

  private async readMeta(key: string): Promise<PersistedMeta | null> {
    const root = this.root();
    const metaName = safeFilename(key) + META_SUFFIX;
    let file: TizenFile;
    try {
      file = root.resolve(metaName);
    } catch {
      return null;
    }
    try {
      const text = await readTextFromFile(file);
      return JSON.parse(text) as PersistedMeta;
    } catch {
      return null;
    }
  }

  async get(key: string): Promise<Blob | null> {
    await this.init();
    const meta = await this.readMeta(key);
    if (!meta) return null;
    const root = this.root();
    let bytesFile: TizenFile;
    try {
      bytesFile = root.resolve(meta.filename);
    } catch {
      // metadata orphan; clean and return miss.
      await this.remove(key);
      return null;
    }
    const bytes = await readBytesFromFile(bytesFile);
    return new Blob([new Uint8Array(bytes)], {
      type: meta.contentType ?? 'application/octet-stream',
    });
  }

  async put(key: string, body: Blob, contentType?: string): Promise<StorageEntry> {
    await this.init();
    const root = this.root();
    const filename = safeFilename(key) + '.bin';
    const metaFilename = safeFilename(key) + META_SUFFIX;
    const ct = contentType ?? body.type ?? 'application/octet-stream';

    // Drop old versions if present (Tizen createFile fails on existing)
    try {
      root.deleteFile(filename);
    } catch {
      /* not present */
    }
    try {
      root.deleteFile(metaFilename);
    } catch {
      /* not present */
    }

    const bytesFile = root.createFile(filename);
    const metaFile = root.createFile(metaFilename);

    const bytes = await blobToBytes(body);
    await writeBytesToFile(bytesFile, bytes);

    const meta: PersistedMeta = {
      key,
      size: body.size,
      contentType: ct,
      storedAt: Date.now(),
      filename,
    };
    const metaBytes = new TextEncoder().encode(JSON.stringify(meta));
    await writeBytesToFile(metaFile, metaBytes);

    return {
      key: meta.key,
      size: meta.size,
      contentType: meta.contentType,
      storedAt: meta.storedAt,
    };
  }

  async remove(key: string): Promise<void> {
    await this.init();
    const root = this.root();
    const meta = await this.readMeta(key);
    if (meta) {
      try {
        root.deleteFile(meta.filename);
      } catch {
        /* already gone */
      }
    }
    try {
      root.deleteFile(safeFilename(key) + META_SUFFIX);
    } catch {
      /* already gone */
    }
  }

  async list(): Promise<StorageEntry[]> {
    await this.init();
    const root = this.root();
    const children = await listChildren(root);
    const out: StorageEntry[] = [];
    for (const file of children) {
      if (!file.name.endsWith(META_SUFFIX)) continue;
      try {
        const text = await readTextFromFile(file);
        const m = JSON.parse(text) as PersistedMeta;
        out.push({
          key: m.key,
          size: m.size,
          contentType: m.contentType,
          storedAt: m.storedAt,
        });
      } catch {
        // Skip corrupted meta entries silently.
      }
    }
    return out;
  }

  async getQuota(): Promise<{ usage: number; quota: number }> {
    return new Promise((resolve) => {
      tizen!.systeminfo.getPropertyValue(
        'STORAGE',
        (info) => {
          const internal = info.units.find((u) => u.type === 'INTERNAL');
          if (!internal) {
            resolve({ usage: -1, quota: -1 });
            return;
          }
          resolve({
            usage: internal.capacity - internal.availableCapacity,
            quota: internal.capacity,
          });
        },
        () => resolve({ usage: -1, quota: -1 }),
      );
    });
  }
}

// ── System info ─────────────────────────────────────────────────────

const APP_START = Date.now();

class TizenSystemInfoImpl implements PlatformSystemInfo {
  readonly available = true;

  async collect(): Promise<SystemInfo> {
    const [storage, cpu, memory, build, display] = await Promise.all([
      this.getProp<TizenSystemInfoStorage>('STORAGE'),
      this.getProp<TizenSystemInfoCpu>('CPU'),
      this.getProp<TizenSystemInfoMemory>('MEMORY'),
      this.getProp<TizenSystemInfoBuild>('BUILD'),
      this.getProp<TizenSystemInfoDisplay>('DISPLAY'),
    ]);

    let diskUsedPercent = -1;
    let diskFreeBytes = -1;
    if (storage) {
      const internal = storage.units.find((u) => u.type === 'INTERNAL');
      if (internal && internal.capacity > 0) {
        diskFreeBytes = internal.availableCapacity;
        diskUsedPercent = Math.round(
          ((internal.capacity - internal.availableCapacity) / internal.capacity) * 100,
        );
      }
    }

    return {
      diskUsedPercent,
      diskFreeBytes,
      ramUsedBytes: memory?.size ?? -1,
      cpuLoad: cpu?.load ?? -1,
      appUptimeHours: (Date.now() - APP_START) / 3_600_000,
      firmwareVersion: build?.buildVersion ?? '',
      deviceModel: build?.model ?? '',
      displayResolution: display
        ? { width: display.resolutionWidth, height: display.resolutionHeight }
        : null,
    };
  }

  private getProp<T>(prop: TizenSystemInfoProperty): Promise<T | null> {
    return new Promise((resolve) => {
      try {
        tizen!.systeminfo.getPropertyValue<T>(
          prop,
          (val) => resolve(val),
          () => resolve(null),
        );
      } catch {
        resolve(null);
      }
    });
  }
}

// ── Power ───────────────────────────────────────────────────────────

class TizenPowerImpl implements PlatformPower {
  readonly available = true;

  async keepScreenOn(): Promise<PowerLock> {
    try {
      tizen!.power.request('SCREEN', 'SCREEN_NORMAL');
    } catch (err) {
      console.warn('[platform/tizen] power.request failed', err);
    }
    return {
      release: () => {
        try {
          tizen!.power.release('SCREEN');
        } catch {
          /* ignore */
        }
      },
    };
  }
}

// ── Network ─────────────────────────────────────────────────────────

class TizenNetworkImpl implements PlatformNetwork {
  readonly available = true;

  async info(): Promise<NetworkInfo> {
    const type = await this.getNetType();
    let online = false;
    let ipAddress: string | undefined;
    let macAddress: string | undefined;
    try {
      online = tizen!.network.isConnectedToGateway();
      ipAddress = tizen!.network.getIpAddress();
      macAddress = tizen!.network.getMACAddress();
    } catch {
      // tizen.network may not be exposed on every model — fallback to onLine.
      online = navigator.onLine;
    }
    return { type, online, ipAddress, macAddress };
  }

  private getNetType(): Promise<NetworkType> {
    return new Promise((resolve) => {
      tizen!.systeminfo.getPropertyValue(
        'NETWORK',
        (info) => {
          const t = info.networkType?.toUpperCase() ?? '';
          if (t.startsWith('WIFI')) resolve('wifi');
          else if (t.startsWith('ETHERNET')) resolve('ethernet');
          else if (t === 'NONE') resolve('offline');
          else if (t === '2G' || t === '2.5G' || t === '3G' || t === '4G') {
            resolve('cellular');
          } else resolve('unknown');
        },
        () => resolve('unknown'),
      );
    });
  }

  onChange(cb: (info: NetworkInfo) => void): () => void {
    // Tizen has no first-class network change listener exposed in JS;
    // fall back to navigator events which DO fire inside the Tizen webview.
    const emit = () => {
      void this.info().then(cb);
    };
    window.addEventListener('online', emit);
    window.addEventListener('offline', emit);
    return () => {
      window.removeEventListener('online', emit);
      window.removeEventListener('offline', emit);
    };
  }
}

// ── Application ─────────────────────────────────────────────────────

class TizenApplicationImpl implements PlatformApplication {
  readonly available = true;

  async info() {
    const app = tizen!.application.getCurrentApplication();
    return {
      appId: app.appInfo.id,
      version: app.appInfo.version,
      packageId: app.appInfo.packageId,
    };
  }

  exit(): void {
    try {
      tizen!.application.getCurrentApplication().exit();
    } catch (err) {
      console.warn('[platform/tizen] application.exit failed', err);
      // Fallback — at least try to refresh the widget.
      window.location.reload();
    }
  }
}

// ── Alarm ───────────────────────────────────────────────────────────
//
// tizen.alarm fires by launching the app — it does NOT call back into
// JS directly. For an in-process scheduled callback we therefore use
// setTimeout in tandem with alarm for cold-start reliability. Pure
// setTimeout would also work but loses wake guarantee if the screen-
// saver kicks in. This hybrid keeps both behaviours.

class TizenAlarmImpl implements PlatformAlarm {
  readonly available = true;

  async scheduleRelative(
    delaySec: number,
    callback: () => void,
  ): Promise<AlarmHandle> {
    const timer = setTimeout(callback, delaySec * 1000);
    let tizenId: string | null = null;
    try {
      const alarm = new tizen!.AlarmRelative(delaySec);
      const appId = tizen!.application.getCurrentApplication().appInfo.id;
      tizenId = tizen!.alarm.add(alarm, appId);
    } catch {
      // tizen.alarm may not be available on every model — JS timer alone.
    }
    return {
      id: tizenId ?? `timer-${Date.now()}`,
      cancel: () => {
        clearTimeout(timer);
        if (tizenId) {
          try {
            tizen!.alarm.remove(tizenId);
          } catch {
            /* ignore */
          }
        }
      },
    };
  }
}

// ── Public ──────────────────────────────────────────────────────────

export const tizenPlatform: PlatformApi = {
  kind: 'tizen',
  storage: new TizenStorage(),
  systemInfo: new TizenSystemInfoImpl(),
  power: new TizenPowerImpl(),
  network: new TizenNetworkImpl(),
  application: new TizenApplicationImpl(),
  alarm: new TizenAlarmImpl(),
};
